import { components, paths, CustomerStatus } from './schema';
import { UncapitalizeKeys, OptionalNullable, OverrideType } from '@/types/utils';
import { TXCenter } from '@/api/apis/facilities';
import { TXCenterDataToUpdate } from '@/types/customer';

export { CustomerStatus, CustomerOrderByOptions } from './schema.d';

export enum CustomerRoleEnum {
  TxCenterAdministrator = 'customer:tx-center-administrator',
  TxCenterCoordinator = 'customer:tx-center-coordinator',
  ReceivingSurgeon = 'customer:receiving-surgeon',
  TxCenterTransplantSupport = 'customer:transplant-support',
  AdminAuditor = 'customer:admin-auditor',
}

export enum CustomerPermissionEnum {
  TxCenterQuoteApprover = 'customer:quote-approver',
  TxCenterOrganApprover = 'customer:organ-approver',
  TxCenterOfferCoordinator = 'customer:tx-center-offer-coordinator',
}

export enum OpoRoleEnum {
  OpoAdmin = 'opo:admin',
  OpoRetrievalSurgeon = 'opo:retrieval-surgeon',
  OpoTransplantCoordinator = 'opo:transplant-coordinator',
  OpoTransplantSupport = 'opo:transplant-support',
}

export const CustomerRole = {
  ...CustomerRoleEnum,
  ...CustomerPermissionEnum,
};

export type CustomerRoleT = CustomerRoleEnum | CustomerPermissionEnum | OpoRoleEnum;

export type CustomerRoleOnly = CustomerRoleEnum;
export type CustomerPermissionOnly = CustomerPermissionEnum;
export type OpoRoleOnly = OpoRoleEnum;

type CustomerResponse = OptionalNullable<components['schemas']['CustomerResponse']>;

type WithOverriddenRoles<T> = OverrideType<T, { roles?: CustomerRoleT[]; permissions?: CustomerPermissionEnum[] }>;
type WithOverriddenRole<T> = OverrideType<T, { role?: CustomerRoleT[] }>;

export type Customer = WithOverriddenRoles<CustomerResponse>;
export type TxCustomer = WithOverriddenRole<CustomerResponse>;

export type PaginatedCustomerListQueryFilters = UncapitalizeKeys<
  Required<paths['/customers']['get']['parameters']>['query']
>;

export type CustomerListQueryFilters = Pick<
  PaginatedCustomerListQueryFilters,
  'txCenters' | 'roles' | 'isPrimary' | 'isVerified' | 'enabled'
>;

// TODO: Replace CustomerListQueryFilters after Api change
export type CustomerListFilters = {
  txCenters?: string[];
  roles?: CustomerRoleT[];
  isPrimary?: boolean;
  isVerified?: boolean;
  enabled?: boolean;
};

export type CreateCustomerRequestBody = WithOverriddenRoles<
  OptionalNullable<components['schemas']['CreateCustomerRequest']>
>;

export type PatchCustomerRequestBody = WithOverriddenRoles<
  OptionalNullable<components['schemas']['PatchCustomerRequest']>
>;

export type TxCenterInfo = {
  customerId: string;
  txCenterId: string;
  txCenterName: string;
  txCenterCode: string;
  roles: CustomerRoleT[];
  permissions?: string[];
  isPrimary: boolean;
  txCenterCountryCode?: string;
  txCenter?: Pick<TXCenter, 'id' | 'name' | 'code' | 'allowedEmailDomains'>;
  txCenterTrialPrograms?: string[];
};

// TODO: Confirm field names with backend when customerInOpos is available on the profile
export type OpoInfo = {
  customerId: string;
  opoId: string;
  opoName: string;
  opoCode: string;
  roles: CustomerRoleT[];
  isPrimary: boolean;
};

export type CustomerMT = Omit<Customer, 'txCenterId' | 'roles' | 'isPrimary'> & {
  customerInTxCenters: TxCenterInfo[];
  customerInOpos?: OpoInfo[];
  isOpoUser?: boolean; // This might be changed depends on the backend implementation
  status?: CustomerStatus;
  txCentersList?: TXCenter[];
};

export type CreateTxUser = {
  txCenterId?: string;
  roles?: CustomerRoleT[];
  permissions?: CustomerPermissionEnum[];
} & Omit<CustomerMT, 'customerInTxCenters' | 'status' | 'id' | 'enabled'>;

export type UpdateTxUser = Omit<CustomerMT, 'customerInTxCenters'> & {
  customerInTxCenters?: TXCenterDataToUpdate[];
};
